import { useMemo, useState } from "react";
import { FaSort } from "react-icons/fa";

export const MONTHS = [
    { key: "month01", label: "Enero" },
    { key: "month02", label: "Febrero" },
    { key: "month03", label: "Marzo" },
    { key: "month04", label: "Abril" },
    { key: "month05", label: "Mayo" },
    { key: "month06", label: "Junio" },
    { key: "month07", label: "Julio" },
    { key: "month08", label: "Agosto" },
    { key: "month09", label: "Septiembre" },
    { key: "month10", label: "Octubre" },
    { key: "month11", label: "Noviembre" },
    { key: "month12", label: "Diciembre" },
];

// Money: siempre 2 decimales, punto decimal, sin separador de miles.
export const formatNumber = (n) => (Number(n) || 0).toFixed(2);

export const rowTotal = (row, months) =>
    months.reduce((sum, m) => sum + (Number(row?.[m.key]) || 0), 0);

export default function BudgetGrid({ categories, values, onChangeCell, onSave, saving, dirty, readOnly = false }) {
    const [sortAsc, setSortAsc] = useState(true);

    // Solo se muestra del mes actual en adelante — los meses ya transcurridos no se editan.
    const visibleMonths = useMemo(() => MONTHS.slice(new Date().getMonth()), []);

    const sortedCategories = useMemo(() => {
        const list = [...categories];
        list.sort((a, b) => {
            const cmp = (a.categoryName || "").localeCompare(b.categoryName || "");
            return sortAsc ? cmp : -cmp;
        });
        return list;
    }, [categories, sortAsc]);

    const columnTotals = useMemo(() => {
        const totals = {};
        visibleMonths.forEach((m) => {
            totals[m.key] = categories.reduce(
                (sum, c) => sum + (Number(values[c.categoryId]?.[m.key]) || 0),
                0
            );
        });
        return totals;
    }, [categories, values, visibleMonths]);

    const grandTotal = visibleMonths.reduce((sum, m) => sum + columnTotals[m.key], 0);
    const registrosConPresupuesto = categories.filter(
        (c) => rowTotal(values[c.categoryId], visibleMonths) > 0
    ).length;

    const stickyCol2 = "sticky left-0 z-20 w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]";

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-gray-100 text-gray-700 text-left">
                            <th
                                className={`px-3 py-2.5 font-semibold whitespace-nowrap cursor-pointer select-none bg-gray-100 ${stickyCol2}`}
                                onClick={() => setSortAsc((s) => !s)}
                            >
                                <span className="inline-flex items-center gap-1">
                                    Nombres <FaSort className="text-gray-400" size={11} />
                                </span>
                            </th>
                            {visibleMonths.map((m) => (
                                <th
                                    key={m.key}
                                    className="px-3 py-2.5 font-semibold text-right whitespace-nowrap border-l border-gray-200 min-w-[130px]"
                                >
                                    {m.label}
                                </th>
                            ))}
                            <th className="px-3 py-2.5 font-semibold text-right whitespace-nowrap border-l border-gray-200">
                                Total
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedCategories.map((cat, idx) => {
                            const row = values[cat.categoryId] || {};
                            const rowBg = idx % 2 === 0 ? "bg-white" : "bg-gray-50";
                            return (
                                <tr key={cat.categoryId} className={`${rowBg} hover:bg-brand-primary/5`}>
                                    <td className={`px-3 py-2.5 whitespace-nowrap font-medium text-gray-800 ${rowBg} ${stickyCol2}`}>
                                        {cat.categoryName}
                                    </td>
                                    {visibleMonths.map((m) => (
                                        <td key={m.key} className="px-1.5 py-1 border-l border-gray-100 min-w-[130px]">
                                            {readOnly ? (
                                                <div className="w-full text-right px-2.5 py-2 text-gray-600 tabular-nums">
                                                    {row[m.key] ? formatNumber(row[m.key]) : ""}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="w-full text-right px-2.5 py-2 rounded-md border border-gray-200 bg-white hover:border-gray-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/20 focus:outline-none transition-colors"
                                                    value={row[m.key] || ""}
                                                    onFocus={(e) => e.target.select()}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;
                                                        // Solo digitos, un unico punto decimal, y maximo 2 decimales
                                                        let sanitized = raw
                                                            .replace(/[^0-9.]/g, "")
                                                            .replace(/(\..*)\./g, "$1");
                                                        const dotIndex = sanitized.indexOf(".");
                                                        if (dotIndex !== -1) {
                                                            sanitized = sanitized.slice(0, dotIndex + 3);
                                                        }
                                                        onChangeCell(cat.categoryId, m.key, sanitized);
                                                    }}
                                                    onPaste={(e) => {
                                                        const pasted = e.clipboardData.getData("text");
                                                        if (!/^\d*\.?\d{0,2}$/.test(pasted)) e.preventDefault();
                                                    }}
                                                    onBlur={() => {
                                                        const num = Number(row[m.key]);
                                                        if (num) onChangeCell(cat.categoryId, m.key, num.toFixed(2));
                                                    }}
                                                />
                                            )}
                                        </td>
                                    ))}
                                    <td className="px-3 py-2.5 text-right font-semibold whitespace-nowrap border-l border-gray-100">
                                        {formatNumber(rowTotal(row, visibleMonths))}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="bg-amber-100 font-bold text-gray-800">
                            <td className={`px-3 py-2.5 whitespace-nowrap bg-amber-100 ${stickyCol2}`}>
                                {registrosConPresupuesto} Registros
                            </td>
                            {visibleMonths.map((m) => (
                                <td key={m.key} className="px-3 py-2.5 text-right whitespace-nowrap border-l border-amber-200">
                                    {formatNumber(columnTotals[m.key])}
                                </td>
                            ))}
                            <td className="px-3 py-2.5 text-right whitespace-nowrap border-l border-amber-200">
                                {formatNumber(grandTotal)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {readOnly ? (
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50 text-xs text-gray-400">
                    Vista global (suma de todas las sucursales) — solo lectura.
                </div>
            ) : (
                <div className="flex justify-end px-4 py-3 border-t border-gray-200 bg-gray-50/50">
                    <button
                        disabled={!dirty || saving}
                        onClick={onSave}
                        className={`px-5 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition ${
                            !dirty || saving
                                ? "bg-gray-300 cursor-not-allowed"
                                : "bg-brand-primary hover:opacity-90"
                        }`}
                    >
                        {saving ? "Guardando..." : "Guardar cambios"}
                    </button>
                </div>
            )}
        </div>
    );
}
